import { forwardRef, PropsWithChildren, useMemo, useState } from "react";
/* eslint-disable */
import * as Select from "@radix-ui/react-select";
import { X } from "lucide-react";
import cx from "classnames";
import styles from "./Select.module.scss";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

export type SelectOption = {
  label: string;
  value: string;
};

interface SelectItemProps {
  value: string;
  selected?: boolean;
  className?: string;
  onSelect: (value: string) => void;
}

const SelectItem = forwardRef(function SelectItem(
  {
    children,
    value,
    selected = false,
    className,
    onSelect,
  }: PropsWithChildren<SelectItemProps>,
  forwardedRef: React.Ref<HTMLDivElement>
) {
  return (
    <div
      ref={forwardedRef as React.RefObject<HTMLDivElement>}
      className={cx(styles.Item, className)}
      onClick={() => onSelect(value)}
    >
      <Checkbox
        checked={selected}
        className="mr-2 flex-shrink-0"
        onCheckedChange={() => onSelect(value)}
      />
      <span className="truncate w-full">{children}</span>
    </div>
  );
});

interface SelectProps {
  options?: SelectOption[];
  value?: string[];
  onChange: (value: string[]) => void;
  size?: "small" | "medium";
  placeholder?: string;
  className?: string;
  classes?: {
    trigger?: string;
  };
}

function MultiSelect({
  options = [],
  value = [],
  onChange,
  size = "medium",
  placeholder = "Select …",
  className,
  classes = {},
}: SelectProps) {
  const [open, setOpen] = useState(false);

  const selectedOptions = useMemo(() => {
    return options.filter((option) => value?.includes(option.value));
  }, [options, value]);

  const handleSelect = (selectedValue: string) => {
    const newValues = value?.includes(selectedValue)
      ? value.filter((v) => v !== selectedValue)
      : [...(value || []), selectedValue];
    onChange(newValues);
  };

  const handleRemoveChip = (chipValue: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newValues = (value || []).filter((v) => v !== chipValue);
    onChange(newValues);
  };

  return (
    <Select.Root open={open} onOpenChange={setOpen}>
      <Select.Trigger asChild>
        <div className={cx(styles.Select, className)}>
          <div
            className={cx(
              styles.Trigger,
              styles[size],
              classes.trigger,
              "border border-input hover:border-input-hover rounded-md px-3 py-2"
            )}
          >
            <div className="flex items-center justify-between w-full gap-2">
              <div className="flex flex-wrap gap-1.5 min-w-0 flex-1 py-0.5 pl-1 pr-2">
                {selectedOptions.length === 0 && (
                  <span className="text-muted-foreground truncate select-none">
                    {placeholder}
                  </span>
                )}
                {selectedOptions.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="max-w-[200px] truncate flex items-center gap-1 py-0 h-6 px-2"
                  >
                    <span className="truncate">{option.label}</span>
                  </Badge>
                ))}
              </div>
              <ChevronDownIcon className={styles.Icon} />
            </div>
          </div>
        </div>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className={cx(
            styles.Content,
            "border border-input rounded-md bg-background shadow-md z-[9999]"
          )}
          position="popper"
          sideOffset={4}
          align="start"
          style={{ zIndex: 9999 }}
        >
          <Select.Viewport className={styles.Viewport}>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                selected={value?.includes(option.value)}
                onSelect={handleSelect}
              >
                {option.label}
              </SelectItem>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

export default MultiSelect;
